import { makeObservable, observable, action, computed } from 'mobx';
import { logger } from '../utils/logger';

/**
 * 用户信息更新存储
 * 专门管理SSE推送的用户信息更新，包括昵称、头像、消息等
 * 不直接修改原始ERP数据，而是维护一个独立的更新映射
 */
class UserInfoUpdateStore {
  constructor() {
    // 用户信息更新映射：userId -> 更新信息
    this._userInfoUpdates = new Map();

    // 用户消息更新映射：userId -> 最新消息信息
    this._userMessageUpdates = new Map();

    // 用户未读消息计数映射：userId -> 未读数量
    this._userUnreadCounts = new Map();

    // 更新版本号：用于跟踪是否有更新
    this._updateVersion = 0;

    // 正在添加的用户集合，防止重复添加
    this._addingUsers = new Set();

    makeObservable(this, {
      _userInfoUpdates: observable,
      _userMessageUpdates: observable,
      _userUnreadCounts: observable,
      _updateVersion: observable,
      _addingUsers: observable,

      // 计算属性
      hasUpdates: computed,
      updateCount: computed,

      // 动作方法
      updateUserInfo: action,
      updateUserMessage: action,
      updateUserUnreadCount: action,
      clearUserUpdates: action,
      clearAllUpdates: action,
      _checkAndAddNewUser: action,
      _addNewUserToList: action,
      _checkAndMoveUserToTop: action,
    });
  }

  // ==================== 计算属性 ====================

  get hasUpdates() {
    // 使用更新版本号来确保每次更新都能触发重新计算
    return this._updateVersion > 0;
  }

  get updateCount() {
    return this._userInfoUpdates.size + this._userMessageUpdates.size + this._userUnreadCounts.size;
  }

  // ==================== 核心方法 ====================

  /**
   * 更新用户基本信息（昵称、头像等）
   * @param {string} userId - 用户ID
   * @param {Object} userInfo - 用户信息对象
   */
  updateUserInfo = (userId, userInfo) => {
    if (!userId || !userInfo) {
      logger.warn('[UserInfoUpdateStore] updateUserInfo 参数不完整', { userId, userInfo });
      return;
    }

    const normalizedUserId = String(userId);
    const existingInfo = this._userInfoUpdates.get(normalizedUserId) || {};

    // 合并更新信息
    const updatedInfo = {
      ...existingInfo,
      ...userInfo,
      // 记录更新时间
      updatedAt: Date.now(),
    };

    this._userInfoUpdates.set(normalizedUserId, updatedInfo);

    logger.debug('[UserInfoUpdateStore] 用户信息已更新', {
      userId: normalizedUserId,
      userName: userInfo.userName,
      chatUserName: userInfo.chatUserName,
      hasAvatar: !!userInfo.userAvatar,
      updateKeys: Object.keys(userInfo),
    });

    // 检查是否需要添加新用户到列表
    this._checkAndAddNewUser(normalizedUserId, userInfo);
  };

  /**
   * 更新用户消息信息
   * @param {string} userId - 用户ID
   * @param {Object} messageInfo - 消息信息对象
   */
  updateUserMessage = (userId, messageInfo) => {
    if (!userId || !messageInfo) {
      logger.warn('[UserInfoUpdateStore] updateUserMessage 参数不完整', { userId, messageInfo });
      return;
    }

    const normalizedUserId = String(userId);
    const existingMessage = this._userMessageUpdates.get(normalizedUserId) || {};

    // 检查时间戳是否变化，如果变化则更新版本号
    const timestamp = messageInfo.timestamp || messageInfo.lastReceiveTime;
    const existingTimestamp = existingMessage.timestamp || existingMessage.lastReceiveTime;

    if (timestamp !== existingTimestamp) {
      this._updateVersion++;
      logger.debug('[UserInfoUpdateStore] 消息时间戳变化，更新版本号', {
        userId: normalizedUserId,
        oldTimestamp: existingTimestamp,
        newTimestamp: timestamp,
        version: this._updateVersion,
      });
    }

    // 合并消息信息
    const updatedMessage = {
      ...existingMessage,
      ...messageInfo,
      timestamp: timestamp, // 保存时间戳用于比较
      // 记录更新时间
      updatedAt: Date.now(),
    };

    this._userMessageUpdates.set(normalizedUserId, updatedMessage);

    logger.debug('[UserInfoUpdateStore] 用户消息已更新', {
      userId: normalizedUserId,
      lastMessage: messageInfo.content,
      timestamp: timestamp,
      messageType: messageInfo.type,
      version: this._updateVersion,
    });

    // 检查是否需要移动用户到顶部
    this._checkAndMoveUserToTop(normalizedUserId);
  };

  /**
   * 更新用户未读消息计数
   * @param {string} userId - 用户ID
   * @param {number} unreadCount - 未读消息数量
   */
  updateUserUnreadCount = (userId, unreadCount) => {
    if (!userId || unreadCount === undefined) {
      logger.warn('[UserInfoUpdateStore] updateUserUnreadCount 参数不完整', {
        userId,
        unreadCount,
      });
      return;
    }

    const normalizedUserId = String(userId);
    const existingCount = this._userUnreadCounts.get(normalizedUserId);

    // 检查未读计数是否变化，如果变化则更新版本号
    if (existingCount?.count !== unreadCount) {
      this._updateVersion++;
      logger.debug('[UserInfoUpdateStore] 未读计数变化，更新版本号', {
        userId: normalizedUserId,
        oldCount: existingCount?.count,
        newCount: unreadCount,
        version: this._updateVersion,
      });
    }

    this._userUnreadCounts.set(normalizedUserId, {
      count: unreadCount,
      updatedAt: Date.now(),
    });

    logger.debug('[UserInfoUpdateStore] 用户未读计数已更新', {
      userId: normalizedUserId,
      unreadCount,
      version: this._updateVersion,
    });
  };

  /**
   * 获取用户的合并显示信息
   * 将原始用户数据与更新信息合并，返回用于显示的数据
   * @param {Object} originalUser - 原始用户数据
   * @returns {Object} 合并后的用户显示数据
   */
  getUserDisplayInfo = originalUser => {
    if (!originalUser) {
      return null;
    }

    const userId = String(originalUser.chat_user_id);
    const infoUpdates = this._userInfoUpdates.get(userId) || {};
    const messageUpdates = this._userMessageUpdates.get(userId) || {};
    const unreadCountUpdate = this._userUnreadCounts.get(userId);

    // 合并显示信息
    const displayInfo = {
      ...originalUser,
      // 用户信息更新（昵称、头像等）
      user_name: infoUpdates.userName || infoUpdates.nickName || originalUser.user_name,
      chat_user_name:
        infoUpdates.chatUserName ||
        infoUpdates.userName ||
        infoUpdates.nickName ||
        originalUser.chat_user_name,
      avatar_url: infoUpdates.userAvatar || infoUpdates.avatarUrl || originalUser.avatar_url,
      chat_user_avatar:
        infoUpdates.chatUserAvatar ||
        infoUpdates.userAvatar ||
        infoUpdates.avatarUrl ||
        originalUser.chat_user_avatar,

      // 消息信息更新
      last_message:
        messageUpdates.content || messageUpdates.lastMessage || originalUser.last_message,
      last_message_time:
        messageUpdates.timestamp ||
        messageUpdates.lastReceiveTime ||
        originalUser.last_message_time,
      last_receive_time:
        messageUpdates.lastReceiveTime ||
        messageUpdates.timestamp ||
        originalUser.last_receive_time,
      last_message_type:
        messageUpdates.type || messageUpdates.messageType || originalUser.last_message_type,

      // 未读消息计数更新
      unread_count:
        unreadCountUpdate?.count !== undefined
          ? unreadCountUpdate.count
          : originalUser.unread_count,

      // 其他字段更新
      channel: infoUpdates.channel || originalUser.channel,
      serialNumber: infoUpdates.serialNumber || originalUser.serialNumber,

      // 标记是否有更新
      hasInfoUpdates: Object.keys(infoUpdates).length > 0,
      hasMessageUpdates: Object.keys(messageUpdates).length > 0,
      hasUnreadUpdates: unreadCountUpdate !== undefined,
    };

    return displayInfo;
  };

  /**
   * 获取所有用户的合并显示信息
   * @param {Array} originalUsers - 原始用户数据数组
   * @returns {Array} 合并后的用户显示数据数组
   */
  getAllUsersDisplayInfo = originalUsers => {
    if (!Array.isArray(originalUsers)) {
      return [];
    }

    return originalUsers.map(user => this.getUserDisplayInfo(user));
  };

  /**
   * 清除指定用户的更新信息
   * @param {string} userId - 用户ID
   */
  clearUserUpdates = userId => {
    const normalizedUserId = String(userId);
    this._userInfoUpdates.delete(normalizedUserId);
    this._userMessageUpdates.delete(normalizedUserId);
    this._userUnreadCounts.delete(normalizedUserId);

    logger.debug('[UserInfoUpdateStore] 用户更新信息已清除', { userId: normalizedUserId });
  };

  /**
   * 清除所有更新信息
   */
  clearAllUpdates = () => {
    this._userInfoUpdates.clear();
    this._userMessageUpdates.clear();
    this._userUnreadCounts.clear();
    this._updateVersion = 0; // 重置版本号

    logger.debug('[UserInfoUpdateStore] 所有更新信息已清除');
  };

  /**
   * 获取更新统计信息
   * @returns {Object} 统计信息
   */
  getUpdateStats = () => {
    return {
      infoUpdates: this._userInfoUpdates.size,
      messageUpdates: this._userMessageUpdates.size,
      unreadUpdates: this._userUnreadCounts.size,
      totalUpdates: this.updateCount,
      hasUpdates: this.hasUpdates,
    };
  };

  /**
   * 检查用户是否有更新
   * @param {string} userId - 用户ID
   * @returns {boolean} 是否有更新
   */
  hasUserUpdates = userId => {
    const normalizedUserId = String(userId);
    return (
      this._userInfoUpdates.has(normalizedUserId) ||
      this._userMessageUpdates.has(normalizedUserId) ||
      this._userUnreadCounts.has(normalizedUserId)
    );
  };

  /**
   * 检查并添加新用户到列表
   * @param {string} userId - 用户ID
   * @param {Object} userInfo - 用户信息
   */
  _checkAndAddNewUser = (userId, userInfo) => {
    const normalizedUserId = String(userId);

    // 检查是否正在添加该用户，防止重复添加
    if (this._addingUsers.has(normalizedUserId)) {
      logger.debug('[UserInfoUpdateStore] 用户正在添加中，跳过重复添加', {
        userId: normalizedUserId,
        userName: userInfo.userName,
      });
      return;
    }

    // 动态导入serviceStore避免循环依赖
    import('./ServiceStore')
      .then(({ serviceStore }) => {
        if (!serviceStore?.viewData?.data) {
          logger.warn('[UserInfoUpdateStore] serviceStore.viewData不存在，无法检查新用户', {
            userId,
            hasServiceStore: !!serviceStore,
            hasViewData: !!serviceStore?.viewData,
            hasData: !!serviceStore?.viewData?.data,
          });
          return;
        }

        // 强化去重检查：检查用户是否已存在于原始列表中
        const existingUser = serviceStore.viewData.data.find(
          user => String(user.chat_user_id) === String(userId)
        );

        // 额外检查：确保没有正在添加中的用户
        const isCurrentlyAdding = this._addingUsers.has(normalizedUserId);

        if (!existingUser && !isCurrentlyAdding) {
          // 标记正在添加
          this._addingUsers.add(normalizedUserId);

          // 用户不存在，创建新用户并添加到列表顶部
          logger.info('[UserInfoUpdateStore] 检测到新用户，添加到列表顶部', {
            userId,
            userName: userInfo.userName,
            chatUserName: userInfo.chatUserName,
            hasAvatar: !!userInfo.userAvatar,
          });

          this._addNewUserToList(userId, userInfo, serviceStore);

          // 添加完成后清除标记
          this._addingUsers.delete(normalizedUserId);
        } else {
          logger.debug('[UserInfoUpdateStore] 用户已存在，无需添加', {
            userId,
            existingUserName: existingUser.user_name,
          });
        }
      })
      .catch(error => {
        logger.error('[UserInfoUpdateStore] 导入ServiceStore失败:', error);
        // 出错时也要清除标记
        this._addingUsers.delete(normalizedUserId);
      });
  };

  /**
   * 添加新用户到列表顶部
   * @param {string} userId - 用户ID
   * @param {Object} userInfo - 用户信息
   * @param {Object} serviceStore - ServiceStore实例
   */
  _addNewUserToList = (userId, userInfo, serviceStore) => {
    // 最终去重检查：确保用户确实不存在
    const finalCheck = serviceStore.viewData.data.find(
      user => String(user.chat_user_id) === String(userId)
    );

    if (finalCheck) {
      logger.warn('[UserInfoUpdateStore] 用户已存在，跳过添加', {
        userId,
        existingUserName: finalCheck.user_name,
      });
      return;
    }

    // 获取消息更新信息
    const messageUpdates = this._userMessageUpdates.get(userId) || {};
    const unreadCountUpdate = this._userUnreadCounts.get(userId);

    // 创建新用户对象
    const newUser = {
      chat_user_id: userId,
      user_name: userInfo.userName || userInfo.nickName || `用户${userId}`,
      chat_user_name:
        userInfo.chatUserName || userInfo.userName || userInfo.nickName || `用户${userId}`,
      avatar_url: userInfo.userAvatar || userInfo.avatarUrl || null,
      chat_user_avatar:
        userInfo.chatUserAvatar || userInfo.userAvatar || userInfo.avatarUrl || null,
      last_message: messageUpdates.content || messageUpdates.lastMessage || '暂无消息',
      last_message_time: messageUpdates.timestamp || messageUpdates.lastReceiveTime || Date.now(),
      last_receive_time:
        messageUpdates.lastReceiveTime || messageUpdates.timestamp || new Date().toISOString(),
      unread_count: unreadCountUpdate?.count !== undefined ? unreadCountUpdate.count : 0,
      channel: userInfo.channel || 2, // 默认第三方渠道
      last_message_type: messageUpdates.type || messageUpdates.messageType || 'text',
      // 添加其他必要字段
      serialNumber: userInfo.serialNumber || null,
      current_node_name: null,
      status: 1,
      create_at: new Date().toISOString(),
    };

    // 添加到列表顶部
    serviceStore.viewData.data.unshift(newUser);

    logger.info('[UserInfoUpdateStore] 新用户已添加到列表顶部', {
      userId,
      userName: newUser.user_name,
      chatUserName: newUser.chat_user_name,
      hasAvatar: !!newUser.avatar_url,
      totalUsers: serviceStore.viewData.data.length,
      newUser,
    });
  };

  /**
   * 检查并移动用户到列表顶部
   * @param {string} userId - 用户ID
   */
  _checkAndMoveUserToTop = userId => {
    // 动态导入serviceStore避免循环依赖
    import('./ServiceStore')
      .then(({ serviceStore }) => {
        if (!serviceStore?.viewData?.data) {
          logger.warn('[UserInfoUpdateStore] serviceStore.viewData不存在，无法移动用户', {
            userId,
            hasServiceStore: !!serviceStore,
            hasViewData: !!serviceStore?.viewData,
            hasData: !!serviceStore?.viewData?.data,
          });
          return;
        }

        // 查找用户索引
        const userIndex = serviceStore.viewData.data.findIndex(
          user => String(user.chat_user_id) === userId
        );

        if (userIndex !== -1 && userIndex !== 0) {
          // 用户存在且不在顶部，移动到顶部
          const user = serviceStore.viewData.data[userIndex];
          serviceStore.viewData.data.splice(userIndex, 1);
          serviceStore.viewData.data.unshift(user);

          logger.info('[UserInfoUpdateStore] 用户已移动到列表顶部', {
            userId,
            userName: user.user_name,
            oldIndex: userIndex,
            newIndex: 0,
          });
        } else if (userIndex === 0) {
          logger.debug('[UserInfoUpdateStore] 用户已在列表顶部', {
            userId,
            userName: serviceStore.viewData.data[0]?.user_name,
          });
        } else {
          logger.debug('[UserInfoUpdateStore] 用户不存在于列表中，将在updateUserInfo中处理', {
            userId,
          });
        }
      })
      .catch(error => {
        logger.error('[UserInfoUpdateStore] 导入ServiceStore失败:', error);
      });
  };
}

// 创建单例实例
export const userInfoUpdateStore = new UserInfoUpdateStore();
